import moment from 'moment';
import React from 'react';
import {
  externalIntegrationUnavailableMessage,
  type ExternalIntegrationStatus,
} from '@hxr/config/external-integrations';
import { getConfiguredStaticURL } from '../config/runtime.js';
import {
  addDownloadRecord,
  getAllVersions,
  getPclabDistributionAvailability,
  getUpdatedFiles,
} from '../services/pclab.js';
import { getSession } from '../services/register.js';
import { alertError, alertInfo } from '../utils/edittools.js';
import { responseCode, responseData, responseMessage } from '../types/domain.js';
import type { SoftwareFileInfo, SoftwareFileMap } from '../types/domain.js';
import {
  advapiTip,
  chromeBrowserUpdate,
  closeAntivirusTip,
  servicePack1Tip,
  visualCppTip,
  webView2Tip,
  webView2Tip2,
  win10Error0xc000007b,
  win7Certs,
  win7CrtTip,
  win7UpdateServer,
  windowsFirewall,
  winUpdateServer,
} from './pclabTips.js';
import type { OperationTip } from './pclabTips.js';
import { isRecord } from './dva-types.js';
import type { ModelSaga, TypedModel } from './dva-types.js';
import { compareSetupVersions } from '../utils/pclabTarget.js';

export interface DownloadInfo {
  /** 完整 mode（如 `teacher2-win-x64`；无后缀记录为其原值 `teacher2`）。 */
  readonly mode?: string;
  readonly version?: string;
  readonly filename?: string;
}

export interface VersionRow extends SoftwareFileInfo {
  readonly index: number;
  readonly key: number;
}

export interface PclabIntroState {
  readonly fileInfo?: SoftwareFileMap;
  readonly versionList?: readonly VersionRow[];
  readonly downloadModal?: boolean;
  readonly downloadInfo?: DownloadInfo;
  readonly authorizationCode?: string;
  readonly userCode?: string;
  readonly downloadLoading?: Readonly<Record<string, boolean>>;
  readonly downLoadVisible?: boolean;
  readonly systemVisible?: boolean;
  readonly errorArray?: readonly OperationTip[];
  readonly historyVersionVisible?: boolean;
  readonly availability?: ExternalIntegrationStatus;
  readonly availabilityChecked?: boolean;
}

function isSessionWithServerTime(value: unknown): value is { serverTime?: number } {
  return (
    !(value instanceof Error) &&
    isRecord(value) &&
    (value['serverTime'] === undefined || typeof value['serverTime'] === 'number')
  );
}

function isPclabIntroState(value: unknown): value is PclabIntroState {
  return isRecord(value);
}

function runtimeUnavailable(availability: ExternalIntegrationStatus): ExternalIntegrationStatus {
  return {
    id: 'pclab-distribution',
    mode: availability.mode,
    available: false,
    reason: 'upstream_unavailable',
  };
}

const model: TypedModel<PclabIntroState> = {
  namespace: 'pclabIntro2',
  state: { availabilityChecked: false } satisfies PclabIntroState,
  subscriptions: {
    setup({ dispatch, history }) {
      history.listen(({ pathname }) => {
        if (pathname !== '/pclab2' && pathname !== '/pclab2/') return;
        dispatch({ type: 'initialization' });
      });
    },
  },
  effects: {
    *initialization(_, { put }): ModelSaga {
      const availability = (yield getPclabDistributionAvailability()) as
        ExternalIntegrationStatus | undefined;
      yield put({ type: 'update', payload: { availability, availabilityChecked: true } });
      const session = yield getSession();
      const serverTime = isSessionWithServerTime(session) ? session.serverTime : undefined;
      if (serverTime !== undefined && Math.abs(Date.now() - serverTime) > 24 * 60 * 60 * 1000) {
        alertInfo('氦星人检测到您的计算机时间异常，可能影响系统正常使用，请您及时调整。');
      }
      if (availability?.available !== true) return;
      const response = yield getUpdatedFiles({ appVersion: 'pclab2' });
      if (responseCode(response) !== 0) {
        alertError(responseMessage(response));
        if (availability?.available) {
          yield put({
            type: 'update',
            payload: {
              availability: runtimeUnavailable(availability),
              downloadModal: false,
              historyVersionVisible: false,
            },
          });
        }
        return;
      }
      yield put({ type: 'update', payload: { fileInfo: responseData<SoftwareFileMap>(response) } });
    },

    *downloadRecord({ payload }, { put, select }): ModelSaga {
      const source = (payload ?? {}) as { schoolSlug?: string; username?: string };
      const selectedState = yield select(
        (globalState: { pclabIntro2: PclabIntroState }) => globalState.pclabIntro2,
      );
      if (!isPclabIntroState(selectedState)) {
        throw new TypeError('Invalid pclabIntro2 state');
      }
      const state = selectedState;
      if (state.availability?.available !== true) {
        alertError(
          state.availability
            ? externalIntegrationUnavailableMessage(state.availability)
            : '暂时无法确认 PCLab 下载服务状态',
        );
        return;
      }
      const { mode, version, filename } = state.downloadInfo ?? {};
      const loadingKey = String(mode);
      yield put({ type: 'update', payload: { downloadLoading: { [loadingKey]: true } } });
      const response = yield addDownloadRecord({
        schoolSlug: source.schoolSlug,
        username: source.username,
        mode,
        type: 'pclab2',
        version,
        code: state.userCode,
      });
      if (responseCode(response) !== 0) {
        alertError(responseMessage(response));
        yield put({
          type: 'update',
          payload: {
            downloadLoading: { [loadingKey]: false },
            downloadModal: false,
            historyVersionVisible: false,
            ...(state.availability?.available
              ? { availability: runtimeUnavailable(state.availability) }
              : {}),
          },
        });
        return;
      }

      const errorArray: OperationTip[] = [];
      const updatedAt = responseData<{ updated_at: string }>(response)?.updated_at;
      if (updatedAt && Math.abs(Date.now() - new Date(updatedAt).getTime()) > 3600000) {
        errorArray.push({
          title: '系统时间误差',
          description: <div>系统时间与东八时区的区时误差超过1小时，请校正系统时间</div>,
        });
      }
      errorArray.push(
        servicePack1Tip,
        webView2Tip,
        webView2Tip2,
        advapiTip,
        win7CrtTip,
        visualCppTip,
        win7Certs,
        closeAntivirusTip,
        winUpdateServer,
        win7UpdateServer,
        chromeBrowserUpdate,
        windowsFirewall,
        win10Error0xc000007b,
      );
      yield put({
        type: 'update',
        payload: {
          downloadModal: false,
          downloadLoading: { [loadingKey]: false },
          errorArray,
        },
      });
      const downloadUrl = `${getConfiguredStaticURL(
        `/file/pclab2/${mode}/${version}/${filename}`,
      )}?date=${moment().format('YYYYMMDDHH')}`;
      window.open(downloadUrl);
    },

    *historyVersion({ payload }, { put, select }): ModelSaga {
      const selectedState = yield select(
        (globalState: { pclabIntro2: PclabIntroState }) => globalState.pclabIntro2,
      );
      if (!isPclabIntroState(selectedState) || selectedState.availability?.available !== true) {
        alertError(
          isPclabIntroState(selectedState) && selectedState.availability
            ? externalIntegrationUnavailableMessage(selectedState.availability)
            : '暂时无法确认 PCLab 下载服务状态',
        );
        return;
      }
      const { mode, modes } = payload as { mode?: string; modes?: readonly string[] };
      // 分节传入的聚合 mode 列表（如 win-x86 节的 `teacher2-win-x86` +
      // 无后缀 `teacher2`）；逐 mode 拉取后按版本号降序合并展示，每条
      // 记录保留自身 mode 供下载 URL 与上报使用。
      const modeList = modes ?? (mode === undefined ? [] : [mode]);
      yield getSession();
      const collected: SoftwareFileInfo[] = [];
      for (const modeKey of modeList) {
        const response = yield getAllVersions({ mode: modeKey });
        if (responseCode(response) !== 0) {
          alertError(responseMessage(response));
          if (selectedState.availability?.available) {
            yield put({
              type: 'update',
              payload: {
                availability: runtimeUnavailable(selectedState.availability),
                downloadModal: false,
                historyVersionVisible: false,
              },
            });
          }
          return;
        }
        collected.push(...(responseData<SoftwareFileInfo[]>(response) ?? []));
      }
      collected.sort((left, right) => compareSetupVersions(right.version, left.version));
      const versionList = collected.map((version, index) => ({
        ...version,
        index: index + 1,
        key: index + 1,
      }));
      yield put({ type: 'update', payload: { versionList } });
    },
  },
  reducers: {
    update(
      state: PclabIntroState,
      { payload }: { payload: Partial<PclabIntroState> },
    ): PclabIntroState {
      return { ...state, ...payload };
    },
  },
};

export default model;
